import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Search, Download, Zap, Shield, Clock, Star, Image, HardDrive, Database, ArrowRight } from 'lucide-react'
import { useCacheStats } from '../hooks/useCacheStats'

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const { cacheStats } = useCacheStats()

  const features = [
    {
      icon: Zap,
      title: '高效浏览',
      description: '无限滚动浏览所有剧集和图片，告别繁琐的翻页操作'
    },
    {
      icon: Download,
      title: '批量下载',
      description: '选择多张图片一键下载，自动打包成ZIP文件'
    },
    {
      icon: Clock,
      title: '智能缓存',
      description: '本地缓存机制，二次访问速度更快'
    },
    {
      icon: Shield,
      title: '安全可靠',
      description: '代理下载，保护您的网络安全'
    }
  ]

  const steps = [
    {
      number: 1,
      title: '搜索内容',
      description: '输入动漫、电影或TV剧名称进行搜索'
    },
    {
      number: 2,
      title: '浏览剧集',
      description: '查看所有剧集，选择您感兴趣的内容'
    },
    {
      number: 3,
      title: '选择图片',
      description: '浏览所有截图，勾选需要的图片'
    },
    {
      number: 4,
      title: '批量下载',
      description: '一键下载选中的图片，自动打包'
    }
  ]

  return (
    <div className="space-y-12">
      {/* 英雄区域 */}
      <section className="text-center py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Fancap
            <span className="text-primary-600">下载器</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            为 Fancaps.net 用户提供高效、流畅的图片浏览和批量下载体验，
            解决原网站分页加载带来的操作繁琐问题
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {/* 本地图片库卡片 */}
            <div
              onClick={() => navigate('/local-library')}
              className="group cursor-pointer bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6 hover:from-green-100 hover:to-green-200 hover:border-green-300 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="bg-green-600 text-white p-3 rounded-lg group-hover:bg-green-700 transition-colors">
                    <Database className="w-6 h-6" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-green-900 group-hover:text-green-800">
                      本地图片库
                    </h3>
                    <p className="text-green-700 text-sm">
                      浏览已缓存的 {cacheStats?.total_images || 0} 张图片
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-green-600 group-hover:text-green-700 transition-colors" />
              </div>
              
              <div className="space-y-2 text-sm text-green-700">
                <div className="flex items-center justify-between">
                  <span>🎯 零网络请求</span>
                  <span className="font-medium">极速加载</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>💾 本地缓存</span>
                  <span className="font-medium">{cacheStats?.size_human || '0 MB'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>⚡ 立即可用</span>
                  <span className="font-medium text-green-600">推荐使用</span>
                </div>
              </div>
            </div>

            {/* 搜索历史分类库卡片 */}
            <div
              onClick={() => navigate('/search-based-library')}
              className="group cursor-pointer bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 hover:from-orange-100 hover:to-orange-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <div className="bg-orange-600 text-white p-3 rounded-lg group-hover:bg-orange-700 transition-colors">
                    <Search className="w-6 h-6" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-orange-900 group-hover:text-orange-800">
                      搜索历史分类库
                    </h3>
                    <p className="text-orange-700 text-sm">
                      基于搜索历史智能创建分类
                    </p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-orange-600 group-hover:text-orange-700 transition-colors" />
              </div>
              
              <div className="space-y-2 text-sm text-orange-700">
                <div className="flex items-center justify-between">
                  <span>🔍 搜索记录</span>
                  <span className="font-medium">自动分类</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>📝 历史整理</span>
                  <span className="font-medium">按作品集数</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>🤖 智能管理</span>
                  <span className="font-medium text-orange-600">SMART</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 功能特性 */}
      <section id="features" className="py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            为什么选择 Fancap下载器？
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            我们致力于为用户提供最佳的图片浏览和下载体验
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="card p-6 text-center hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <feature.icon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {feature.title}
              </h3>
              <p className="text-gray-600">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 使用步骤 */}
      <section className="py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            简单四步，轻松下载
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            跟随我们的简单指引，快速上手使用
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                {step.number}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {step.title}
              </h3>
              <p className="text-gray-600">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 行动召唤 */}
      <section className="py-12 bg-primary-50 rounded-2xl">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            准备开始了吗？
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            立即开始搜索您喜欢的动漫、电影或TV剧，体验全新的图片浏览和下载方式
          </p>
          <Link
            to="/search"
            className="btn btn-primary text-lg px-8 py-3 inline-flex items-center"
          >
            <Search className="w-5 h-5 mr-2" />
            立即开始搜索
          </Link>
        </div>
      </section>
    </div>
  )
}

export default HomePage 